import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box, Typography, Button, Paper } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { getSectionData } from "../utils/app.utils";
import { SectionContent } from "./SectionContent";
import SectionInfoPanel from "./SectionInfoPanel";

type SectionPanelProps = {
  questionnaire: {
    id: number;
    name: string;
    short_name?: string;
    privacy_notice?: string;
    create_date?: string;
    last_change_date?: string;
    ai_prompt?: string;
    anonymous_flag: boolean;
  };
  onSectionComplete?: (data: any) => void;
  isLast?: boolean;
};

const SectionPanel: React.FC<SectionPanelProps> = ({
  questionnaire,
  onSectionComplete,
  isLast,
}) => {
  const [sectionAnswers, setSectionAnswers] = useState<Record<number, any>>({});

  const [sections, setSections] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const methods = useForm({ mode: "onChange" });

  const { data: sectionData = [] } = useQuery({
    queryKey: ["sectionLabels", questionnaire?.id],
    queryFn: () => getSectionData(questionnaire?.id),
  });

  useEffect(() => {
    if (sectionData.length) {
      setSections(sectionData);
      setTab(0);
    }
  }, [sectionData]);

  const setAnswer = (questionId: number, value: any) => {
    setSectionAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleNextTab = () => {
    if (tab < sections.length - 1) {
      setTab((prev) => prev + 1);
    }
  };

  const onSubmit = () => {
    onSectionComplete?.({
      questionnaireId: questionnaire.id,
      sectionId: sections[tab].id,
      answers: sectionAnswers,
    });

    //clear answers of the submitted section
    setSectionAnswers({});
  };

  return (
    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      {/* Left side: Section info panel */}
      {sections.length > 0 && (
        <Box
          component={Paper}
          sx={{
            flex: { xs: "1 1 100%", md: "1 1 30%" },
            minWidth: { md: 300 },
            p: 3,
            borderRadius: 2,
            boxSizing: "border-box",
          }}
        >
          <SectionInfoPanel section={sections[tab]} />
        </Box>
      )}

      {/* Right side: Form */}
      <Box
        component={Paper}
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 65%" },
          minWidth: 0,
          p: 3,
          borderRadius: 2,
          boxSizing: "border-box",
          minHeight: 400,
        }}
      >
        <FormProvider {...methods}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((section) => (
              <Tab key={section.id} label={section.name} />
            ))}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {sections.length === 0 ? (
              <Typography>No sections available</Typography>
            ) : (
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <SectionContent
                  section={sections[tab]}
                  answers={sectionAnswers}
                  onAnswerChange={setAnswer}
                />

                {tab < sections.length - 1 && (
                  <Box sx={{ mt: 3, textAlign: "right" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNextTab}
                    >
                      WEITER
                    </Button>
                  </Box>
                )}

                {tab === sections.length - 1 && (
                  <Box sx={{ mt: 3, textAlign: "right" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                    >
                      {isLast ? "Absenden" : "Weiter"}
                    </Button>
                  </Box>
                )}
              </form>
            )}
          </Box>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default SectionPanel;
